import type { PackageDocument } from '../content/document_schema';
import { api, ApiError } from './client';
import { useAuth } from '../stores/auth';

export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Membership {
  org_id: string;
  org_slug: string;
  org_name: string;
  role: Role;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    is_active: boolean;
    is_platform_admin: boolean;
    last_login_at: string | null;
    created_at: string;
  };
  memberships: Membership[];
}

export interface LoginResponse {
  token: string;
  user: { id: string; email: string; name: string | null; is_platform_admin: boolean };
  memberships: Membership[];
}

export interface Device {
  id: string;
  name: string;
  hardware_id: string;
  status: string;
  last_seen_at: string | null;
  paired_at: string | null;
  pairing_code?: string;
  tags: Record<string, string>;
  metadata: Record<string, unknown>;
  created_at: string;
  schedule_id?: string | null;
  schedule_name?: string | null;
  playlist_id?: string | null;
  playlist_name?: string | null;
  /** Phase 3c.6 — current LAN IP reported by the Pi on each heartbeat.
   *  Only present on GET /devices/:id, not in the list endpoint. */
  local_ip?: string | null;
  /** Phase 3c.6 — public/NAT egress IP captured server-side from the
   *  heartbeat HTTP connection. Same scope as local_ip. */
  external_ip?: string | null;
  sync_status?:
    | { state: 'idle' }
    | { state: 'syncing'; version_id: string; bytes_done: number; bytes_total: number; percent: number; started_at: string }
    | { state: 'recently_completed'; version_id: string; bytes_total: number; completed_at: string }
    | { state: 'recently_failed'; version_id: string; reason: string; failed_at: string };
}

// Phase 3c.4 — device telemetry samples for the overview page.
export interface DeviceMetricSample {
  ts:              string;
  cpu_pct:         number | null;
  mem_pct:         number | null;
  cpu_temp_c:      number | null;
  disk_used_b:     number | null;
  disk_total_b:    number | null;
  uptime_s:        number | null;
  net_in_b:        number | null;
  net_out_b:       number | null;
  renderer_mem_b:  number | null;
}

export interface DeviceMetricsResponse {
  deviceId:    string;
  windowHours: number;
  samples:     DeviceMetricSample[];
  latest:      DeviceMetricSample | null;
}

// Phase 3c.5 — device snapshots (kiosk screenshots).
export interface DeviceSnapshot {
  id:                  string;
  captured_at:         string;
  width:               number | null;
  height:              number | null;
  size_b:              number | null;
  trigger:             'on_demand' | 'package_load';
  package_version_id:  string | null;
  /** Inline base64 JPEG (data:image/jpeg;base64,...) — null if file missing.
   *  Inlined to avoid Authorization-header issues on <img src=...>. */
  data_url:            string | null;
}

export interface DeviceSnapshotsResponse {
  snapshots: DeviceSnapshot[];
}

export interface Package {
  id: string;
  name: string;
  description: string | null;
  versions: Array<{ id: string; version: number; size_bytes: number; created_at: string; total_duration_s?: number | null }> | null;
  created_at: string;
}

export interface Playlist {
  id: string;
  name: string;
  items: Array<{ package_version_id: string; duration_s: number; settings?: Record<string, unknown> }>;
  created_at: string;
}

export interface Schedule {
  id: string;
  name: string;
  rules: Array<{
    playlist_id: string;
    days: number[];
    start_time: string;
    end_time: string;
    priority: number;
  }>;
  default_playlist_id: string | null;
  created_at: string;
}

export interface Member {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  role: Role;
  is_platform_admin: boolean;
  joined_at: string;
  last_login_at: string | null;
  created_at: string;
}

export interface Invite {
  id: string;
  email: string;
  role: Role;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  invited_by_email: string | null;
}

export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  member_count: number;
  device_count: number;
  package_count: number;
  /** Phase 3d.1 — per-org storage quota in bytes. Sent as string by the
   *  pg driver to preserve bigint precision. */
  storage_quota_bytes?: string;
  /** Phase 3d.1 — live-aggregated bytes consumed by this org across
   *  packages, assets, and snapshots. Same string treatment. */
  bytes_used?: string;
}

export interface PlatformUser {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  is_platform_admin: boolean;
  last_login_at: string | null;
  created_at: string;
  memberships: Array<{ org_id: string; org_slug: string; org_name: string; role: Role }>;
}

// ---- Auth ----
export const auth = {
  login: (p: { email: string; password: string }) =>
    api<LoginResponse>('/auth/login', { method: 'POST', body: p, orgIdOverride: null }),
  me: () => api<MeResponse>('/auth/me', { orgIdOverride: null }),
  changePassword: (p: { currentPassword: string; newPassword: string }) =>
    api<{ ok: true }>('/auth/change-password', { method: 'POST', body: p, orgIdOverride: null }),
  inviteInfo: (token: string) =>
    api<{ email: string; role: Role; orgSlug: string; orgName: string; expiresAt: string; userExists: boolean }>(
      `/auth/invite-info?token=${encodeURIComponent(token)}`,
      { orgIdOverride: null }
    ),
  acceptInvite: (p: { token: string; password?: string; name?: string }) =>
    api<{ token: string; userId: string; orgId: string }>('/auth/accept-invite', {
      method: 'POST',
      body: p,
      orgIdOverride: null,
    }),
};

// ---- Per-org Members ----
export const members = {
  list: () => api<{ members: Member[] }>('/members'),
  updateRole: (userId: string, role: Role) =>
    api(`/members/${userId}`, { method: 'PATCH', body: { role } }),
  remove: (userId: string) => api(`/members/${userId}`, { method: 'DELETE' }),
};

// ---- Per-org Invites ----
export const invites = {
  list: () => api<{ invites: Invite[] }>('/invites'),
  create: (p: { email: string; role: Exclude<Role, 'owner'>; expiresInHours?: number }) =>
    api<{ invite: Invite; claimUrl: string }>('/invites', { method: 'POST', body: p }),
  revoke: (id: string) => api(`/invites/${id}`, { method: 'DELETE' }),
};

// ---- Devices ----
export const devices = {
  list: () => api<{ devices: Device[] }>('/devices'),
  get: (id: string) => api<{ device: Device }>(`/devices/${id}`),
  // Phase 3c.3 redesign: the admin no longer creates a placeholder device
  // with a server-generated code. Instead the Pi shows its own code, admin
  // types it into the UI and claims it.
  claimPairing: (p: { pairingCode: string; name: string; tags?: Record<string, string> }) =>
    api<{ device: Device }>('/devices/claim-pairing', { method: 'POST', body: p }),
  assign: (id: string, scheduleId: string | null) =>
    api<{ ok: true }>(`/devices/${id}/assign`, { method: 'POST', body: { scheduleId } }),
  remove: (id: string) => api(`/devices/${id}`, { method: 'DELETE' }),
  events: (id: string) =>
    api<{ events: Array<{ id: number; kind: string; payload: unknown; created_at: string }> }>(
      `/devices/${id}/events`
    ),
  // Phase 3c.4 — last `hours` (default 48) of telemetry samples.
  metrics: (id: string, hours = 48) =>
    api<DeviceMetricsResponse>(`/devices/${id}/metrics?hours=${hours}`),

  // Phase 3c.5 — kiosk screenshots. JPEG bytes ship inline as base64
  // data_url on each snapshot; no separate /:id/snapshots/:snapshotId
  // fetch needed. Eliminates the missing_token bug on <img src=...>.
  snapshots: (id: string, limit = 5) =>
    api<DeviceSnapshotsResponse>(`/devices/${id}/snapshots?limit=${limit}`),
  /** Push a screenshot_request WS command to the device. Returns immediately;
   *  the actual JPEG arrives via the agent → server upload pipeline. */
  requestSnapshot: (id: string) =>
    api<{ ok: true; status: 'capture_requested' }>(
      `/devices/${id}/snapshots/capture`,
      { method: 'POST' }
    ),
  deleteSnapshot: (deviceId: string, snapshotId: string) =>
    api(`/devices/${deviceId}/snapshots/${snapshotId}`, { method: 'DELETE' }),

  // Phase 3c.7 — WebSSH. Two-step: ticket from HTTP, then WS upgrade.
  // Ticket is single-use and expires in 60s.
  shellTicket: (id: string) =>
    api<{ ticket: string; ttlSeconds: number }>(
      `/devices/${id}/shell-ticket`,
      { method: 'POST' }
    ),
  shellSessions: (id: string) =>
    api<{ sessions: ShellSession[] }>(`/devices/${id}/shell-sessions`),
};

export interface ShellSession {
  id:         string;
  started_at: string;
  ended_at:   string | null;
  end_reason: string | null;
  client_ip:  string | null;
  user_email: string | null;
  user_name:  string | null;
}

// ---- Packages ----
export const packages = {
  list: () => api<{ packages: Package[] }>('/packages'),
  create: (p: { name: string; description?: string }) =>
    api<{ package: Package }>('/packages', { method: 'POST', body: p }),
  uploadVersion: async (packageId: string, bundle: File, manifest: unknown) => {
    const form = new FormData();
    form.append('bundle', bundle);
    form.append('manifest', JSON.stringify(manifest));
    return api<{ version: { id: string; version: number } }>(`/packages/${packageId}/versions`, {
      method: 'POST',
      body: form,
    });
  },
};

// ---- Playlists ----
export const playlists = {
  list: () => api<{ playlists: Playlist[] }>('/playlists'),
  create: (p: { name: string; items: Playlist['items'] }) =>
    api<{ playlist: Playlist }>('/playlists', { method: 'POST', body: p }),
  update: (id: string, p: Partial<{ name: string; items: Playlist['items'] }>) =>
    api(`/playlists/${id}`, { method: 'PUT', body: p }),
  remove: (id: string) => api(`/playlists/${id}`, { method: 'DELETE' }),
};

// ---- Schedules ----
export const schedules = {
  list: () => api<{ schedules: Schedule[] }>('/schedules'),
  create: (p: { name: string; rules: Schedule['rules']; default_playlist_id: string | null }) =>
    api<{ schedule: Schedule }>('/schedules', { method: 'POST', body: p }),
  update: (
    id: string,
    p: Partial<{ name: string; rules: Schedule['rules']; default_playlist_id: string | null }>
  ) => api(`/schedules/${id}`, { method: 'PUT', body: p }),
  remove: (id: string) => api(`/schedules/${id}`, { method: 'DELETE' }),
};

// ---- Agent releases ----
export interface AgentRelease {
  id: string;
  version: string;
  channel: string;
  sha256: string;
  size_bytes: number;
  notes: string | null;
  released_at: string;
  released_by_email: string | null;
  assigned_device_count: number;
  applied_device_count: number;
}
export interface AgentDeployment {
  device_id: string;
  device_name: string;
  status: string;
  last_seen_at: string | null;
  reported_version: string | null;
  release_id: string | null;
  assigned_version: string | null;
  assigned_at: string | null;
  applied_at: string | null;
  applied_version: string | null;
}
export const agentReleases = {
  list: () => api<{ releases: AgentRelease[] }>('/agent/releases'),
  upload: async (file: File, version: string, notes?: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('version', version);
    if (notes) form.append('notes', notes);
    return api<{ release: AgentRelease }>('/agent/releases', { method: 'POST', body: form });
  },
  remove: (id: string) => api(`/agent/releases/${id}`, { method: 'DELETE' }),
  deploy: (release_id: string, device_ids: string[]) =>
    api<{ ok: true; release_version: string; devices_targeted: number }>(
      '/agent/releases/deploy',
      { method: 'POST', body: { release_id, device_ids } }
    ),
  unassign: (device_ids: string[]) =>
    api<{ ok: true; cleared: number }>('/agent/releases/unassign',
      { method: 'POST', body: { device_ids } }),
  deployments: () => api<{ deployments: AgentDeployment[] }>('/agent/releases/deployments'),
};

// ---- Platform admin ----
export const platform = {
  orgs: {
    list: () => api<{ orgs: OrgSummary[] }>('/platform/orgs', { orgIdOverride: null }),
    create: (p: { slug: string; name: string }) =>
      api<{ org: OrgSummary }>('/platform/orgs', { method: 'POST', body: p, orgIdOverride: null }),
    update: (id: string, p: { name?: string; storage_quota_bytes?: string }) =>
      api(`/platform/orgs/${id}`, { method: 'PATCH', body: p, orgIdOverride: null }),
    remove: (id: string) =>
      api(`/platform/orgs/${id}`, { method: 'DELETE', orgIdOverride: null }),
    members: (orgId: string) =>
      api<{ members: Member[] }>(`/platform/orgs/${orgId}/members`, { orgIdOverride: null }),
    addMember: (orgId: string, p: { user_id: string; role: Role }) =>
      api(`/platform/orgs/${orgId}/members`, { method: 'POST', body: p, orgIdOverride: null }),
    // Platform member edit — Phase 3d.3
    updateMember: (orgId: string, userId: string, p: { role: Role }) =>
      api(`/platform/orgs/${orgId}/members/${userId}`, { method: 'PATCH', body: p, orgIdOverride: null }),
    removeMember: (orgId: string, userId: string) =>
      api(`/platform/orgs/${orgId}/members/${userId}`, { method: 'DELETE', orgIdOverride: null }),
  },
  users: {
    list: () => api<{ users: PlatformUser[] }>('/platform/users', { orgIdOverride: null }),
    create: (p: {
      email: string;
      name: string;
      password: string;
      is_platform_admin?: boolean;
      memberships?: Array<{ org_id: string; role: Role }>;
    }) => api<{ user_id: string }>('/platform/users', { method: 'POST', body: p, orgIdOverride: null }),
    update: (id: string, p: Partial<{ name: string; is_active: boolean; is_platform_admin: boolean; password: string }>) =>
      api(`/platform/users/${id}`, { method: 'PATCH', body: p, orgIdOverride: null }),
    remove: (id: string) => api(`/platform/users/${id}`, { method: 'DELETE', orgIdOverride: null }),
  },
  stats: () =>
    api<{
      stats: {
        orgs: number;
        users: number;
        platform_admins: number;
        devices: number;
        packages: number;
        package_versions: number;
        playlists: number;
        schedules: number;
      };
    }>('/platform/stats', { orgIdOverride: null }),
};

export interface MonitoringKpis {
  uptime_s: number;
  active_sessions: number;
  req_per_s: number;
  avg_response_ms: number;
  error_rate_pct: number;
  errors_logged: number;
  cpu_load_1m: number;
  memory_used_mb: number;
  memory_total_mb: number;
  memory_free_mb: number;
  heap_used_mb: number;
  heap_total_mb: number;
  node_version: string;
  platform: string;
  disk_used_pct: number | null;
  disk_free_gb: number | null;
  restart_count: number;
  pg_connections: number;
  logins_failed_24h: number;
}

export interface MonitoringOverview {
  window: string;
  kpis: MonitoringKpis;
  http: {
    total: number;
    per_status_class: { '2xx': number; '3xx': number; '4xx': number; '5xx': number };
    top_endpoints: Array<{ path: string; hits: number; avg_ms: number }>;
    status_codes: Array<{ status: number; count: number }>;
  };
}

export interface MonitoringSession {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  is_platform_admin: boolean;
  ip: string | null;
  user_agent: string | null;
  active: boolean;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
}

export interface MonitoringError {
  id: number;
  occurred_at: string;
  method: string | null;
  path: string | null;
  status: number | null;
  user_email: string | null;
  org_slug: string | null;
  message: string;
  stack: string | null;
  meta: unknown;
}

export interface MonitoringRequest {
  ts: number;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  user_id: string | null;
  ip: string | null;
}

export interface MonitoringDatabase {
  pool: { total: number; idle: number; waiting: number };
  connections: number;
  active_queries: number;
  db_size_bytes: number;
  tables: Array<{ table: string; size_bytes: number; row_estimate: number }>;
}

export interface MonitoringSystem {
  system: {
    uptime_s: number;
    cpu_load_1m: number;
    cpu_count: number;
    memory_used_mb: number;
    memory_total_mb: number;
    memory_free_mb: number;
    heap_used_mb: number;
    heap_total_mb: number;
    node_version: string;
    platform: string;
    disk_used_pct: number | null;
    disk_free_gb: number | null;
    restart_count: number;
  };
}

export const monitoring = {
  overview: (window: string) =>
    api<MonitoringOverview>(`/platform/monitoring/overview?window=${window}`, { orgIdOverride: null }),
  system: () => api<MonitoringSystem>('/platform/monitoring/system', { orgIdOverride: null }),
  database: () => api<MonitoringDatabase>('/platform/monitoring/database', { orgIdOverride: null }),
  sessions: (includeInactive: boolean) =>
    api<{ sessions: MonitoringSession[] }>(
      `/platform/monitoring/sessions${includeInactive ? '?include=all' : ''}`,
      { orgIdOverride: null }
    ),
  terminateSession: (id: string) =>
    api(`/platform/monitoring/sessions/${id}/terminate`, { method: 'POST', orgIdOverride: null }),
  errors: () =>
    api<{ errors: MonitoringError[] }>('/platform/monitoring/errors', { orgIdOverride: null }),
  clearErrors: () =>
    api('/platform/monitoring/errors', { method: 'DELETE', orgIdOverride: null }),
  requests: (window: string) =>
    api<{ window: string; total_in_window: number; requests: MonitoringRequest[] }>(
      `/platform/monitoring/requests?window=${window}`,
      { orgIdOverride: null }
    ),
};

export interface Asset {
  id: string;
  name: string;
  kind: 'image' | 'video' | 'pdf';
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  duration_s: number | null;
  sha256: string;
  has_thumbnail: boolean;
  created_at: string;
  uploaded_by_email: string | null;
}

export const assets = {
  list: (kind?: 'image' | 'video' | 'pdf') =>
    api<{ assets: Asset[] }>(`/assets${kind ? `?kind=${kind}` : ''}`),
  upload: async (
    file: File,
    name?: string,
    onProgress?: (loaded: number, total: number) => void,
  ) => {
    return new Promise<{ asset?: Asset; asset_id?: string; deduplicated?: boolean }>((resolve, reject) => {
      const auth = useAuth.getState();
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/v1/assets');
      if (auth.token) xhr.setRequestHeader('Authorization', `Bearer ${auth.token}`);
      if (auth.activeOrgId) xhr.setRequestHeader('X-Org-Id', auth.activeOrgId);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
      };
      xhr.onload = () => {
        let parsed: unknown = null;
        try { parsed = JSON.parse(xhr.responseText); } catch { /* empty */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as { asset?: Asset; asset_id?: string; deduplicated?: boolean });
        } else {
          if (xhr.status === 401) useAuth.getState().logout();
          reject(new ApiError(xhr.status, parsed));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, { error: 'network_error' }));
      xhr.onabort = () => reject(new ApiError(0, { error: 'aborted' }));
      const form = new FormData();
      form.append('file', file);
      if (name) form.append('name', name);
      xhr.send(form);
    });
  },
  rawUrl: (id: string) => {
    // Browser-native <img>/<video> can't send Authorization headers, so we
    // include the JWT and org id in the query string. The /raw endpoint
    // accepts these via dedicated middleware.
    const auth = useAuth.getState();
    const parts: string[] = [];
    if (auth.token) parts.push(`t=${encodeURIComponent(auth.token)}`);
    if (auth.activeOrgId) parts.push(`o=${encodeURIComponent(auth.activeOrgId)}`);
    return `/api/v1/assets/${id}/raw${parts.length ? '?' + parts.join('&') : ''}`;
  },
  remove: (id: string) => api(`/assets/${id}`, { method: 'DELETE' }),
};

export const packageEditor = {
  getDraft: (packageId: string) =>
    api<{ package: { id: string; name: string; description: string | null; created_at: string }; doc: PackageDocument }>(
      `/packages/${packageId}/draft`
    ),
  saveDraft: (packageId: string, doc: PackageDocument) =>
    api<{ ok: true }>(`/packages/${packageId}/draft`, { method: 'PUT', body: { doc } }),
  publish: (packageId: string) =>
    api<{ version: { id: string; version: number; size_bytes: number; created_at: string } }>(
      `/packages/${packageId}/publish`,
      { method: 'POST' }
    ),
};
